/**
 * WHAT: Oracle-specific password policy compliance dashboard.
 * WHY: Oracle has built-in password profiles (unlike PostgreSQL), so this dedicated component
 *      renders profile details, runs CIS benchmark assessments, displays user compliance,
 *      and lets admins customize compliance rules per account type.
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { WideModal } from '../common';

/**
 * WHAT: Oracle-specific password policy compliance dashboard with profiles, CIS benchmark results, user compliance, and override management.
 * WHY: Oracle has built-in password profiles (unlike PostgreSQL), so this dedicated component renders profile details,
 *      runs CIS benchmark assessments, and lets admins customize compliance rules per account type.
 */
const OraclePasswordCompliance = () => {
  const { canViewComplianceReports } = useAuth();

  // State for data
  const [profiles, setProfiles] = useState([]);
  const [complianceResults, setComplianceResults] = useState(null);
  const [benchmarkRules, setBenchmarkRules] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [userCompliance, setUserCompliance] = useState(null);
  const [expandedAccountTypes, setExpandedAccountTypes] = useState(['named']); // Default expand named accounts

  // State for UI
  const [activeTab, setActiveTab] = useState('profiles');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assessmentRunning, setAssessmentRunning] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('cis'); // 'cis' or 'custom'
  const [selectedUserCompliance, setSelectedUserCompliance] = useState(null); // For user compliance detail modal

  // Filter state for compliance results
  const [filters, setFilters] = useState({
    status: '', // 'compliant', 'non-compliant', or '' for all
    severity: '', // 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', or '' for all
    profile: '', // profile name or '' for all
    category: '', // rule category or '' for all
    search: '' // text search
  });

  // Filter state for user compliance
  const [userFilters, setUserFilters] = useState({
    accountType: '', // 'named', 'service', 'system', or '' for all
    compliance: '', // 'compliant', 'non-compliant', 'na', or '' for all
    authType: '', // 'PASSWORD', 'EXTERNAL', 'NONE', or '' for all
    profile: '', // profile name or '' for all
    search: '' // username/email search
  });

  // Override form state
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    rule_id: '',
    override_type: 'SUPPRESS',
    custom_expected_value: '',
    account_types: ['all'],
    justification: '',
    expiry_date: ''
  });
  const [editingOverride, setEditingOverride] = useState(null);

  // Available account types for policies
  const accountTypeOptions = [
    { value: 'all', label: 'All Accounts', description: 'Applies to all account types' },
    { value: 'named', label: 'Named Accounts', description: 'Individual user accounts (human users)' },
    { value: 'application', label: 'Application Accounts', description: 'Accounts used by applications' },
    { value: 'system', label: 'System Accounts', description: 'Built-in database system accounts' },
    { value: 'service', label: 'Service Accounts', description: 'Accounts for background services' },
    { value: 'privileged', label: 'Privileged Accounts', description: 'Accounts with elevated privileges (DBA, SYSDBA)' }
  ];

  // Check permissions
  const hasAccess = canViewComplianceReports('oracle');

  /* WHAT: Fetches Oracle password profile definitions from the backend.
     WHY: Profiles are the foundation of Oracle password policy -- needed to display profile settings and compare against benchmarks. */
  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/databases/compliance/oracle/profiles/');
      if (response.data.success) {
        setProfiles(response.data.profiles || []);
      }
    } catch (err) {
      setError('Failed to fetch Oracle profiles');
      console.error('Error fetching profiles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* WHAT: Fetches per-rule compliance assessment results (compliant/non-compliant per CIS benchmark rule).
     WHY: The results tab shows which password policies pass or fail against CIS or custom benchmarks. */
  const fetchComplianceResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/databases/compliance/oracle/results/');
      if (response.data.success) {
        setComplianceResults(response.data);
      }
    } catch (err) {
      setError('Failed to fetch compliance results');
      console.error('Error fetching compliance results:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* WHAT: Fetches the CIS benchmark rule definitions (expected values, severity, category).
     WHY: Rules are displayed alongside results so users can see what each compliance check expects. */
  const fetchBenchmarkRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/databases/compliance/oracle/rules/');
      if (response.data.success) {
        setBenchmarkRules(response.data.rules || []);
      }
    } catch (err) {
      console.error('Error fetching benchmark rules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* WHAT: Fetches custom compliance overrides (suppress or custom-value rules).
     WHY: Organizations may have legitimate deviations from CIS benchmarks; overrides let admins document and track those exceptions. */
  const fetchOverrides = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/databases/compliance/oracle/overrides/');
      if (response.data.success) {
        setOverrides(response.data.overrides || []);
      }
    } catch (err) {
      console.error('Error fetching overrides:', err);
    }
  }, []);

  /* WHAT: Fetches per-user compliance data grouped by account type (named, service, system).
     WHY: User-level compliance shows which individual accounts are non-compliant, enabling targeted remediation. */
  const fetchUserCompliance = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/databases/compliance/oracle/user-compliance/');
      if (response.data.success) {
        setUserCompliance(response.data);
      }
    } catch (err) {
      console.error('Error fetching user compliance:', err);
    }
  }, []);

  /* WHAT: Triggers a new compliance assessment run on the backend.
     WHY: Assessments must be re-run after profile changes or data refreshes to get up-to-date compliance status. */
  const runAssessment = async () => {
    setAssessmentRunning(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/databases/compliance/oracle/assess/');
      if (response.data.success) {
        // Refresh results after assessment
        await fetchComplianceResults();
        setActiveTab('results');
      } else {
        setError(response.data.error || 'Assessment failed');
      }
    } catch (err) {
      setError('Failed to run assessment');
      console.error('Error running assessment:', err);
    } finally {
      setAssessmentRunning(false);
    }
  };

  /* WHAT: Creates or updates a compliance override (suppress rule or set custom expected value).
     WHY: Admins need to document exceptions to CIS benchmarks with justification and optional expiry for audit trails. */
  const saveOverride = async () => {
    try {
      const payload = {
        ...overrideForm,
        database_type: 'Oracle'
      };

      if (editingOverride) {
        await apiClient.put(`/api/databases/compliance/oracle/overrides/${editingOverride.id}/`, payload);
      } else {
        await apiClient.post('/api/databases/compliance/oracle/overrides/', payload);
      }

      setShowOverrideModal(false);
      setEditingOverride(null);
      setOverrideForm({
        rule_id: '',
        override_type: 'SUPPRESS',
        custom_expected_value: '',
        account_types: ['all'],
        justification: '',
        expiry_date: ''
      });
      await fetchOverrides();
    } catch (err) {
      setError('Failed to save override');
      console.error('Error saving override:', err);
    }
  };

  /* WHAT: Deletes a compliance override after user confirmation.
     WHY: Removing an override restores the original CIS benchmark rule, re-enabling standard compliance checking. */
  const deleteOverride = async (overrideId) => {
    if (!window.confirm('Are you sure you want to delete this override?')) return;

    try {
      await apiClient.delete(`/api/databases/compliance/oracle/overrides/${overrideId}/`);
      await fetchOverrides();
    } catch (err) {
      setError('Failed to delete override');
      console.error('Error deleting override:', err);
    }
  };

  // Load data on mount
  useEffect(() => {
    if (hasAccess) {
      fetchProfiles();
      fetchComplianceResults();
      fetchBenchmarkRules();
      fetchOverrides();
      fetchUserCompliance();
    }
  }, [hasAccess, fetchProfiles, fetchComplianceResults, fetchBenchmarkRules, fetchOverrides, fetchUserCompliance]);

  // Permission denied view
  if (!hasAccess) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          <i className="fas fa-lock me-2"></i>
          You don't have permission to view Oracle compliance reports.
          Please contact your administrator if you need access.
        </div>
      </div>
    );
  }

  /* WHAT: Safely parses a value that may be a JSON string, object, or null into a plain object.
     WHY: Backend returns some fields as JSON strings and others as objects; this normalizes both forms. */
  const parseJSON = (value) => {
    if (!value) return {};
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return value;
  };

  /* WHAT: Converts a benchmark expected-value object (operator + value) into human-readable text like "at least 10".
     WHY: CIS rules store expected values as {operator, value} JSON; users need plain-language descriptions in the UI. */
  const formatExpectedValue = (value) => {
    if (value === null || value === undefined) return 'Not specified';

    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        return value;
      }
    }

    if (typeof parsed !== 'object') return String(parsed);

    const operator = parsed.operator || '==';
    const val = parsed.value;

    // Map operators to readable text
    const operatorMap = {
      '>=': 'at least',
      '<=': 'at most',
      '>': 'more than',
      '<': 'less than',
      '==': 'exactly',
      '!=': 'not',
      'in': 'one of',
      'not_in': 'not one of',
      'contains': 'contains',
      'not_null': 'must be configured',
      'regex': 'matches pattern'
    };

    const readableOp = operatorMap[operator] || operator;

    // Handle special cases
    if (operator === 'not_null') {
      return 'Must be configured (not NULL)';
    }

    if (operator === 'in' && Array.isArray(val)) {
      return `${readableOp}: ${val.join(', ')}`;
    }

    if (val === undefined || val === null) {
      return readableOp;
    }

    return `${readableOp} ${val}`;
  };

  /* WHAT: Extracts a displayable string from an actual compliance value (which may be a primitive, JSON string, or {value} object).
     WHY: Actual values come in multiple shapes from different Oracle profile parameters; this normalizes them for table display. */
  const formatActualValue = (value) => {
    if (value === null || value === undefined) return null;

    // If it's already a primitive, return as string
    if (typeof value !== 'object') return String(value);

    // If it's a string that might be JSON, try to parse
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        return value;
      }
    }

    // If it's an object with a 'value' key, extract that
    if (value && typeof value === 'object') {
      if ('value' in value) return String(value.value);
      // Otherwise stringify it nicely
      return JSON.stringify(value);
    }

    return String(value);
  };

  /* WHAT: Evaluates whether an actual profile value meets a custom override's expected value using operator logic.
     WHY: When admins set custom thresholds (e.g., "password lifetime >= 90" instead of CIS default), the UI must re-evaluate compliance client-side. */
  const checkCustomCompliance = (actualValue, customExpectedValue) => {
    if (actualValue === null || actualValue === undefined) return false;

    let expected;
    try {
      expected = typeof customExpectedValue === 'string' ? JSON.parse(customExpectedValue) : customExpectedValue;
    } catch {
      return String(actualValue) === String(customExpectedValue);
    }

    if (!expected || typeof expected !== 'object') {
      return String(actualValue) === String(customExpectedValue);
    }

    const operator = expected.operator || '==';
    const expectedVal = expected.value;
    const actual = typeof actualValue === 'object' && 'value' in actualValue ? actualValue.value : actualValue;
    const actualNum = parseFloat(actual);
    const expectedNum = parseFloat(expectedVal);

    switch (operator) {
      case '>=': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum >= expectedNum;
      case '<=': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum <= expectedNum;
      case '>': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum > expectedNum;
      case '<': return !isNaN(actualNum) && !isNaN(expectedNum) && actualNum < expectedNum;
      case '==': return String(actual) === String(expectedVal);
      case '!=': return String(actual) !== String(expectedVal);
      case 'not_null': return actual !== null && actual !== undefined && actual !== 'NULL';
      default: return String(actual) === String(expectedVal);
    }
  };

  /* WHAT: Renders a severity level (CRITICAL/HIGH/MEDIUM/LOW) as colored text with a dot indicator.
     WHY: Consistent severity rendering across compliance tables following the DAMA text-color-over-badge styling standard. */
  const renderSeverityBadge = (severity) => {
    const styleMap = {
      'CRITICAL': 'text-danger',
      'HIGH': 'text-warning',
      'MEDIUM': 'text-info',
      'LOW': 'text-secondary'
    };
    const labelMap = {
      'CRITICAL': 'Critical',
      'HIGH': 'High',
      'MEDIUM': 'Medium',
      'LOW': 'Low'
    };
    return (
      <span className={styleMap[severity] || 'text-secondary'}>
        <i className="fas fa-circle fa-xs me-1"></i>
        {labelMap[severity] || severity}
      </span>
    );
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-database me-2 text-danger"></i>
            Oracle Password Policy Compliance
          </h2>
          <p className="text-muted mb-0">
            Review password profiles and CIS benchmark compliance
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={runAssessment}
          disabled={assessmentRunning}
        >
          {assessmentRunning ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Running Assessment...
            </>
          ) : (
            <>
              <i className="fas fa-play me-2"></i>
              Run Assessment
            </>
          )}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'profiles' ? 'active' : ''}`}
            onClick={() => setActiveTab('profiles')}
          >
            <i className="fas fa-user-shield me-2"></i>
            Password Profiles ({profiles.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            <i className="fas fa-clipboard-check me-2"></i>
            Compliance Results
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'rules' ? 'active' : ''}`}
            onClick={() => setActiveTab('rules')}
          >
            <i className="fas fa-book me-2"></i>
            Benchmark Rules ({benchmarkRules.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'overrides' ? 'active' : ''}`}
            onClick={() => setActiveTab('overrides')}
          >
            <i className="fas fa-edit me-2"></i>
            Custom Policies ({overrides.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <i className="fas fa-users me-2"></i>
            User Compliance {userCompliance?.count ? `(${userCompliance.count})` : ''}
          </button>
        </li>
      </ul>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Profiles Tab */}
      {activeTab === 'profiles' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom">
            <h5 className="mb-0">
              <i className="fas fa-user-shield me-2"></i>
              Oracle Password Profiles
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Profile Name</th>
                    <th>Database</th>
                    <th>Host</th>
                    <th>Password Verify Function</th>
                    <th>Password Life Time</th>
                    <th>Password Reuse</th>
                    <th>Failed Login Attempts</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No profiles found. Run a profile collection first.
                      </td>
                    </tr>
                  ) : (
                    profiles.map((profile, idx) => {
                      const passwordPolicy = parseJSON(profile.password_policy);
                      return (
                        <tr key={idx}>
                          <td>
                            <strong>{profile.profile_name}</strong>
                          </td>
                          <td>{profile.db_name || profile.database_identifier}</td>
                          <td>{profile.host_name}</td>
                          <td>
                            {passwordPolicy.password_verify_function === 'NULL' ? (
                              <span className="text-danger">None</span>
                            ) : (
                              <span className="text-success">
                                {passwordPolicy.password_verify_function}
                              </span>
                            )}
                          </td>
                          <td>{passwordPolicy.password_life_time || 'N/A'}</td>
                          <td>
                            Max: {passwordPolicy.password_reuse_max || 'N/A'},
                            Time: {passwordPolicy.password_reuse_time || 'N/A'}
                          </td>
                          <td>{passwordPolicy.failed_login_attempts || 'N/A'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setSelectedProfile(profile)}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Results Tab */}
      {activeTab === 'results' && !loading && complianceResults && (
        <div className="card">
          <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-clipboard-check me-2"></i>
              Compliance Assessment Results
            </h5>
            <div className="btn-group">
              <button
                className={`btn btn-sm ${comparisonMode === 'cis' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setComparisonMode('cis')}
              >
                <i className="fas fa-book me-1"></i>Compare with CIS Benchmark
              </button>
              <button
                className={`btn btn-sm ${comparisonMode === 'custom' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setComparisonMode('custom')}
                title="Compare with your custom policies"
              >
                <i className="fas fa-sliders-h me-1"></i>Compare with Custom Policies
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="card-body border-bottom py-2" style={{ backgroundColor: '#fafafa' }}>
            <div className="row g-2 align-items-center">
              <div className="col-auto">
                <span className="text-muted small fw-bold">Filters:</span>
              </div>
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: '130px' }}
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="">All Status</option>
                  <option value="compliant">Compliant</option>
                  <option value="non-compliant">Non-Compliant</option>
                </select>
              </div>
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: '120px' }}
                  value={filters.severity}
                  onChange={(e) => setFilters({...filters, severity: e.target.value})}
                >
                  <option value="">All Severity</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: '150px' }}
                  value={filters.profile}
                  onChange={(e) => setFilters({...filters, profile: e.target.value})}
                >
                  <option value="">All Profiles</option>
                  {[...new Set((complianceResults.results || []).map(r => r.profile_name))].sort().map(profile => (
                    <option key={profile} value={profile}>{profile}</option>
                  ))}
                </select>
              </div>
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: '150px' }}
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                >
                  <option value="">All Categories</option>
                  {[...new Set((complianceResults.results || []).map(r => r.rule_category).filter(Boolean))].sort().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="col-auto">
                <div className="input-group input-group-sm" style={{ minWidth: '180px' }}>
                  <span className="input-group-text bg-white">
                    <i className="fas fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search rules..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                  />
                </div>
              </div>
              <div className="col-auto ms-auto">
                {(filters.status || filters.severity || filters.profile || filters.category || filters.search) && (
                  <>
                    <span className="badge bg-secondary me-2">
                      {[filters.status, filters.severity, filters.profile, filters.category, filters.search].filter(Boolean).length} active
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setFilters({ status: '', severity: '', profile: '', category: '', search: '' })}
                    >
                      <i className="fas fa-times me-1"></i>Clear All
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Rule Code</th>
                    <th>Rule Name</th>
                    <th>Database</th>
                    <th>Host</th>
                    <th>Profile</th>
                    <th>{comparisonMode === 'cis' ? 'CIS Expected' : 'Custom Expected'}</th>
                    <th>Profile Actual</th>
                    <th>Severity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Apply filters to results
                    let filteredResults = (complianceResults.results || []).map((result, idx) => {
                      // Get custom policy for this rule if in custom mode
                      const customPolicy = comparisonMode === 'custom'
                        ? overrides.find(o => o.rule_id === result.rule_id || o.rule_code === result.rule_code)
                        : null;

                      // Skip if in custom mode and no custom policy exists for this rule
                      if (comparisonMode === 'custom' && !customPolicy) {
                        return null;
                      }

                      // Determine expected value based on comparison mode
                      const expectedValue = comparisonMode === 'custom' && customPolicy
                        ? (customPolicy.override_type === 'SUPPRESS' ? 'Suppressed' : customPolicy.custom_expected_value)
                        : result.expected_value;

                      // Determine compliance based on comparison mode
                      const isCompliant = comparisonMode === 'custom' && customPolicy
                        ? (customPolicy.override_type === 'SUPPRESS' ? true : checkCustomCompliance(result.actual_value, customPolicy.custom_expected_value))
                        : result.is_compliant;

                      return { ...result, customPolicy, expectedValue, isCompliant, originalIdx: idx };
                    }).filter(Boolean);

                    // Apply status filter
                    if (filters.status === 'compliant') {
                      filteredResults = filteredResults.filter(r => r.isCompliant);
                    } else if (filters.status === 'non-compliant') {
                      filteredResults = filteredResults.filter(r => !r.isCompliant);
                    }

                    // Apply severity filter
                    if (filters.severity) {
                      filteredResults = filteredResults.filter(r => r.severity === filters.severity);
                    }

                    // Apply profile filter
                    if (filters.profile) {
                      filteredResults = filteredResults.filter(r => r.profile_name === filters.profile);
                    }

                    // Apply category filter
                    if (filters.category) {
                      filteredResults = filteredResults.filter(r => r.rule_category === filters.category);
                    }

                    // Apply search filter
                    if (filters.search) {
                      const searchLower = filters.search.toLowerCase();
                      filteredResults = filteredResults.filter(r =>
                        (r.rule_code && r.rule_code.toLowerCase().includes(searchLower)) ||
                        (r.rule_name && r.rule_name.toLowerCase().includes(searchLower))
                      );
                    }

                    if (filteredResults.length === 0) {
                      return (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">
                            <i className="fas fa-filter me-2"></i>
                            No results match your filters.
                            {(filters.status || filters.severity || filters.profile || filters.category || filters.search) && (
                              <button
                                className="btn btn-link btn-sm"
                                onClick={() => setFilters({ status: '', severity: '', profile: '', category: '', search: '' })}
                              >
                                Clear filters
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    return filteredResults.map((result) => (
                      <tr key={result.originalIdx} style={!result.isCompliant ? { borderLeft: '3px solid #dc3545' } : {}}>
                        <td><code>{result.rule_code}</code></td>
                        <td>{result.rule_name}</td>
                        <td>{result.database_name || result.db_name || '-'}</td>
                        <td>{result.host_name || result.host || '-'}</td>
                        <td>{result.profile_name}</td>
                        <td>{result.customPolicy?.override_type === 'SUPPRESS' ? <span className="text-muted fst-italic">Suppressed</span> : formatExpectedValue(result.expectedValue)}</td>
                        <td>{formatActualValue(result.actual_value) === null ? <span className="text-muted">NULL</span> : formatActualValue(result.actual_value)}</td>
                        <td>{renderSeverityBadge(result.severity)}</td>
                        <td>
                          {result.isCompliant ? (
                            <span className="text-success">
                              <i className="fas fa-check me-1"></i>Compliant
                            </span>
                          ) : (
                            <span className="text-danger">
                              <i className="fas fa-times me-1"></i>Non-Compliant
                            </span>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Benchmark Rules Tab */}
      {activeTab === 'rules' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom">
            <h5 className="mb-0">
              <i className="fas fa-book me-2"></i>
              CIS Benchmark Rules
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Rule Code</th>
                    <th>Rule Name</th>
                    <th>Category</th>
                    <th>Expected Value</th>
                    <th>Severity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarkRules.map((rule, idx) => (
                    <tr key={idx}>
                      <td><code>{rule.rule_code}</code></td>
                      <td>{rule.rule_name}</td>
                      <td>{rule.rule_category}</td>
                      <td>{formatExpectedValue(rule.expected_value)}</td>
                      <td>{renderSeverityBadge(rule.severity)}</td>
                      <td className="small">{rule.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Custom Policies Tab */}
      {activeTab === 'overrides' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-sliders-h me-2"></i>
              Custom Policies
            </h5>
            <button
              className="btn btn-sm btn-light"
              onClick={() => setShowOverrideModal(true)}
            >
              <i className="fas fa-plus me-2"></i>Add Custom Policy
            </button>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Rule</th>
                    <th>Type</th>
                    <th>Custom Value</th>
                    <th>Applicable To</th>
                    <th>Justification</th>
                    <th>Expiry</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No custom policies configured.
                      </td>
                    </tr>
                  ) : (
                    overrides.map((override, idx) => (
                      <tr key={idx}>
                        <td><code>{override.rule_code}</code></td>
                        <td>
                          <span className={override.override_type === 'SUPPRESS' ? 'text-warning' : 'text-info'}>
                            {override.override_type === 'SUPPRESS' ? 'Suppress' : 'Custom Value'}
                          </span>
                        </td>
                        <td>
                          {override.override_type === 'SUPPRESS' ? (
                            <span className="text-muted fst-italic">-</span>
                          ) : (
                            <span>{formatExpectedValue(override.custom_expected_value)}</span>
                          )}
                        </td>
                        <td>
                          {(override.account_types || ['all']).includes('all') ? (
                            <span className="text-muted">All accounts</span>
                          ) : (
                            <span className="small">
                              {(override.account_types || []).map(t =>
                                accountTypeOptions.find(o => o.value === t)?.label || t
                              ).join(', ')}
                            </span>
                          )}
                        </td>
                        <td className="small">{override.justification}</td>
                        <td>{override.expiry_date || 'Never'}</td>
                        <td>{override.created_by}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => {
                              setEditingOverride(override);
                              setOverrideForm({
                                rule_id: override.rule_id,
                                override_type: override.override_type,
                                custom_expected_value: override.custom_expected_value || '',
                                account_types: override.account_types || ['all'],
                                justification: override.justification,
                                expiry_date: override.expiry_date || ''
                              });
                              setShowOverrideModal(true);
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteOverride(override.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* User Compliance Tab */}
      {activeTab === 'users' && !loading && (
        <div className="card">
          <div className="card-header border-bottom" style={{ backgroundColor: '#e9ecef' }}>
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>
              User Password Policy Compliance - Compared with Custom Policy
            </h5>
          </div>
          <div className="card-body">
            {/* Summary Table */}
            {userCompliance?.summary && (
              <div className="mb-4">
                <h6 className="mb-3" style={{ color: '#495057' }}>Summary by Account Type</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>Account Type</th>
                        <th className="text-center">Total</th>
                        <th className="text-center">Auth: PASSWORD</th>
                        <th className="text-center">Compliant</th>
                        <th className="text-center">Non-Compliant</th>
                        <th className="text-center">N/A*</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'named', label: 'Named Accounts', icon: 'fa-user' },
                        { key: 'service', label: 'Service Accounts', icon: 'fa-cog' },
                        { key: 'system', label: 'System Accounts', icon: 'fa-database' }
                      ].map(({ key, label, icon }) => {
                        const s = userCompliance.summary[key] || {};
                        return (
                          <tr key={key}>
                            <td>
                              <i className={`fas ${icon} me-2 text-muted`}></i>
                              {label}
                            </td>
                            <td className="text-center">{s.total || 0}</td>
                            <td className="text-center">{s.password_auth || 0}</td>
                            <td className="text-center">
                              {s.compliant > 0 && <span className="text-success">{s.compliant}</span>}
                              {!s.compliant && <span className="text-muted">0</span>}
                            </td>
                            <td className="text-center">
                              {s.non_compliant > 0 && <span className="text-danger">{s.non_compliant}</span>}
                              {!s.non_compliant && <span className="text-muted">0</span>}
                            </td>
                            <td className="text-center text-muted">{s.na || 0}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        <td>Total</td>
                        <td className="text-center">{userCompliance.summary.total?.total || 0}</td>
                        <td className="text-center">{userCompliance.summary.total?.password_auth || 0}</td>
                        <td className="text-center text-success">{userCompliance.summary.total?.compliant || 0}</td>
                        <td className="text-center text-danger">{userCompliance.summary.total?.non_compliant || 0}</td>
                        <td className="text-center text-muted">{userCompliance.summary.total?.na || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <small className="text-muted">
                  * N/A = Password policy not applicable (EXTERNAL, NONE, or GLOBAL authentication)
                </small>
              </div>
            )}

            {/* Filters */}
            <div className="mb-3 p-2 border rounded bg-light">
              <div className="row g-2 align-items-center">
                <div className="col-auto">
                  <select
                    className="form-select form-select-sm"
                    value={userFilters.accountType}
                    onChange={(e) => setUserFilters({...userFilters, accountType: e.target.value})}
                    style={{ minWidth: '150px' }}
                  >
                    <option value="">All Account Types</option>
                    <option value="named">Named Accounts</option>
                    <option value="service">Service Accounts</option>
                    <option value="system">System Accounts</option>
                  </select>
                </div>
                <div className="col-auto">
                  <select
                    className="form-select form-select-sm"
                    value={userFilters.compliance}
                    onChange={(e) => setUserFilters({...userFilters, compliance: e.target.value})}
                    style={{ minWidth: '140px' }}
                  >
                    <option value="">All Compliance</option>
                    <option value="compliant">Compliant</option>
                    <option value="non-compliant">Non-Compliant</option>
                    <option value="na">N/A</option>
                  </select>
                </div>
                <div className="col-auto">
                  <select
                    className="form-select form-select-sm"
                    value={userFilters.authType}
                    onChange={(e) => setUserFilters({...userFilters, authType: e.target.value})}
                    style={{ minWidth: '140px' }}
                  >
                    <option value="">All Auth Types</option>
                    <option value="PASSWORD">PASSWORD</option>
                    <option value="EXTERNAL">EXTERNAL</option>
                    <option value="NONE">NONE</option>
                  </select>
                </div>
                <div className="col-auto">
                  <select
                    className="form-select form-select-sm"
                    value={userFilters.profile}
                    onChange={(e) => setUserFilters({...userFilters, profile: e.target.value})}
                    style={{ minWidth: '140px' }}
                  >
                    <option value="">All Profiles</option>
                    {[...new Set((userCompliance?.users || []).map(u => u.profile_name).filter(Boolean))].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="col">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search username, email..."
                    value={userFilters.search}
                    onChange={(e) => setUserFilters({...userFilters, search: e.target.value})}
                  />
                </div>
                {(userFilters.accountType || userFilters.compliance || userFilters.authType || userFilters.profile || userFilters.search) && (
                  <div className="col-auto">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setUserFilters({ accountType: '', compliance: '', authType: '', profile: '', search: '' })}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* User List by Account Type */}
            {(() => {
              const users = userCompliance?.users || [];
              // Apply filters
              const filteredUsers = users.filter(user => {
                if (userFilters.accountType && user.account_type !== userFilters.accountType) return false;
                if (userFilters.authType && user.auth_type !== userFilters.authType) return false;
                if (userFilters.profile && user.profile_name !== userFilters.profile) return false;
                if (userFilters.compliance) {
                  if (userFilters.compliance === 'compliant' && user.is_compliant !== true) return false;
                  if (userFilters.compliance === 'non-compliant' && user.is_compliant !== false) return false;
                  if (userFilters.compliance === 'na' && user.is_compliant !== null) return false;
                }
                if (userFilters.search) {
                  const search = userFilters.search.toLowerCase();
                  const matchesSearch = (
                    user.username?.toLowerCase().includes(search) ||
                    user.enterprise_email?.toLowerCase().includes(search) ||
                    user.enterprise_display_name?.toLowerCase().includes(search)
                  );
                  if (!matchesSearch) return false;
                }
                return true;
              });

              // Group by account type
              const grouped = {
                named: filteredUsers.filter(u => u.account_type === 'named'),
                service: filteredUsers.filter(u => u.account_type === 'service'),
                system: filteredUsers.filter(u => u.account_type === 'system')
              };

              const accountTypeLabels = {
                named: { label: 'Named Accounts', icon: 'fa-user' },
                service: { label: 'Service Accounts', icon: 'fa-cog' },
                system: { label: 'System Accounts', icon: 'fa-database' }
              };

              return (
                <div className="accordion" id="userComplianceAccordion">
                  {Object.entries(grouped).map(([type, typeUsers]) => {
                    const { label, icon } = accountTypeLabels[type];
                    const isExpanded = expandedAccountTypes.includes(type);
                    const compliantCount = typeUsers.filter(u => u.is_compliant === true).length;
                    const nonCompliantCount = typeUsers.filter(u => u.is_compliant === false).length;

                    return (
                      <div className="accordion-item" key={type}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${!isExpanded ? 'collapsed' : ''}`}
                            type="button"
                            style={{ backgroundColor: '#e9ecef' }}
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedAccountTypes(expandedAccountTypes.filter(t => t !== type));
                              } else {
                                setExpandedAccountTypes([...expandedAccountTypes, type]);
                              }
                            }}
                          >
                            <i className={`fas ${icon} me-2`}></i>
                            <strong>{label}</strong>
                            <span className="ms-2 text-muted small">({typeUsers.length})</span>
                            <span className="ms-auto me-3">
                              {compliantCount > 0 && <span className="text-success me-2">{compliantCount} compliant</span>}
                              {nonCompliantCount > 0 && <span className="text-danger">{nonCompliantCount} non-compliant</span>}
                            </span>
                          </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}>
                          <div className="accordion-body p-0">
                            <div className="table-responsive">
                              <table className="table table-sm table-hover mb-0">
                                <thead className="table-dark">
                                  <tr>
                                    <th>Username</th>
                                    <th>Database</th>
                                    <th>Profile</th>
                                    <th>Auth Type</th>
                                    <th>Status</th>
                                    <th>Compliance</th>
                                    {type === 'named' && <th>AD Info</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {typeUsers.length === 0 ? (
                                    <tr>
                                      <td colSpan={type === 'named' ? 7 : 6} className="text-center text-muted py-3">
                                        No users found
                                      </td>
                                    </tr>
                                  ) : (
                                    typeUsers.map((user, idx) => (
                                      <tr key={`${user.username}-${user.db_name}-${idx}`}>
                                        <td>
                                          <code>{user.username}</code>
                                        </td>
                                        <td>
                                          <small>{user.db_name}</small>
                                          {user.host_name && <small className="text-muted d-block">{user.host_name}</small>}
                                        </td>
                                        <td><small>{user.profile_name || '-'}</small></td>
                                        <td>
                                          {user.auth_type === 'PASSWORD' && <span className="text-primary">{user.auth_type}</span>}
                                          {user.auth_type === 'EXTERNAL' && <span className="text-info">{user.auth_type}</span>}
                                          {user.auth_type !== 'PASSWORD' && user.auth_type !== 'EXTERNAL' && <span className="text-muted">{user.auth_type || '-'}</span>}
                                        </td>
                                        <td>
                                          {user.account_status === 'OPEN' && <span className="text-success">OPEN</span>}
                                          {user.account_status === 'LOCKED' && <span className="text-danger">LOCKED</span>}
                                          {user.account_status !== 'OPEN' && user.account_status !== 'LOCKED' && <span className="text-muted">{user.account_status || '-'}</span>}
                                        </td>
                                        <td>
                                          {user.is_compliant === true && (
                                            <a
                                              href="#"
                                              className="text-success"
                                              style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                              onClick={(e) => { e.preventDefault(); setSelectedUserCompliance(user); }}
                                            >
                                              <i className="fas fa-check me-1"></i>
                                              {user.compliant_count}/{user.total_rules}
                                            </a>
                                          )}
                                          {user.is_compliant === false && (
                                            <a
                                              href="#"
                                              className="text-danger"
                                              style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                              onClick={(e) => { e.preventDefault(); setSelectedUserCompliance(user); }}
                                            >
                                              <i className="fas fa-times me-1"></i>
                                              {user.compliant_count}/{user.total_rules}
                                            </a>
                                          )}
                                          {user.is_compliant === null && (
                                            <span className="text-muted">N/A</span>
                                          )}
                                        </td>
                                        {type === 'named' && (
                                          <td>
                                            {user.enterprise_display_name && (
                                              <small>
                                                {user.enterprise_display_name}
                                                {user.department && <span className="text-muted"> ({user.department})</span>}
                                              </small>
                                            )}
                                            {!user.enterprise_display_name && <span className="text-muted">-</span>}
                                          </td>
                                        )}
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* User Compliance Detail Modal */}
      <WideModal
        show={!!selectedUserCompliance}
        onClose={() => setSelectedUserCompliance(null)}
        title={`Compliance Details: ${selectedUserCompliance?.username || ''}`}
        headerIcon="fa-clipboard-check"
        headerClass="bg-secondary text-white"
        maxWidth="900px"
        footer={
          <button className="btn btn-secondary" onClick={() => setSelectedUserCompliance(null)}>Close</button>
        }
      >
        {selectedUserCompliance && (
          <div>
            {/* User Info */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="row">
                <div className="col-md-4">
                  <small className="text-muted">Username</small>
                  <div><strong><code>{selectedUserCompliance.username}</code></strong></div>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Database</small>
                  <div>{selectedUserCompliance.db_name} <span className="text-muted">({selectedUserCompliance.host_name})</span></div>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Profile</small>
                  <div>{selectedUserCompliance.profile_name || '-'}</div>
                </div>
              </div>
            </div>

            {/* Compliance Summary */}
            <div className="mb-3">
              <span className="me-3">
                <i className="fas fa-check-circle text-success me-1"></i>
                <strong>{selectedUserCompliance.compliant_count}</strong> Compliant
              </span>
              <span>
                <i className="fas fa-times-circle text-danger me-1"></i>
                <strong>{selectedUserCompliance.non_compliant_count}</strong> Non-Compliant
              </span>
            </div>

            {/* Compliance Rules Table */}
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Rule</th>
                    <th>Severity</th>
                    <th>Expected</th>
                    <th>Actual</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedUserCompliance.compliance_rules || []).map((rule, idx) => {
                    const expectedVal = (() => {
                      try {
                        const parsed = typeof rule.expected_value === 'string' ? JSON.parse(rule.expected_value) : rule.expected_value;
                        if (parsed?.operator && parsed?.value !== undefined) {
                          return `${parsed.operator} ${parsed.value}${parsed.unit ? ` ${parsed.unit}` : ''}`;
                        }
                        return parsed?.value ?? rule.expected_value;
                      } catch { return rule.expected_value; }
                    })();
                    const actualVal = (() => {
                      try {
                        const parsed = typeof rule.actual_value === 'string' ? JSON.parse(rule.actual_value) : rule.actual_value;
                        const val = parsed?.value;
                        if (val === null || val === undefined) return null;
                        return val;
                      } catch { return rule.actual_value; }
                    })();

                    return (
                      <tr key={idx}>
                        <td><code>{rule.rule_code}</code></td>
                        <td>
                          {rule.severity === 'CRITICAL' && <span className="text-danger">{rule.severity}</span>}
                          {rule.severity === 'HIGH' && <span className="text-danger">{rule.severity}</span>}
                          {rule.severity === 'MEDIUM' && <span className="text-warning">{rule.severity}</span>}
                          {rule.severity === 'LOW' && <span className="text-info">{rule.severity}</span>}
                        </td>
                        <td><small>{expectedVal}</small></td>
                        <td><small>{actualVal !== null ? actualVal : <span className="text-muted">NULL</span>}</small></td>
                        <td className="text-center">
                          {rule.is_compliant ? (
                            <span className="text-success"><i className="fas fa-check"></i></span>
                          ) : (
                            <span className="text-danger"><i className="fas fa-times"></i></span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </WideModal>

      {/* Profile Detail Modal */}
      <WideModal
        show={!!selectedProfile}
        onClose={() => setSelectedProfile(null)}
        title={`Profile Details: ${selectedProfile?.profile_name || ''}`}
        headerIcon="fa-user-shield"
        headerClass="bg-light border-bottom"
        maxWidth="1200px"
        footer={
          <button className="btn btn-secondary" onClick={() => setSelectedProfile(null)}>Close</button>
        }
      >
        {selectedProfile && (
          <div className="row">
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-header bg-light border-bottom">
                  <h6 className="mb-0"><i className="fas fa-key me-2"></i>Password Policy</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm table-striped mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ width: '60%', whiteSpace: 'nowrap' }}>Setting</th>
                        <th style={{ width: '40%' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(parseJSON(selectedProfile.password_policy)).map(([key, value]) => (
                        <tr key={key}>
                          <td className="text-capitalize" style={{ whiteSpace: 'nowrap' }}>{key.replace(/_/g, ' ')}</td>
                          <td><strong>{formatActualValue(value)}</strong></td>
                        </tr>
                      ))}
                      {Object.keys(parseJSON(selectedProfile.password_policy)).length === 0 && (
                        <tr><td colSpan="2" className="text-muted text-center">No policy data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-header bg-light border-bottom">
                  <h6 className="mb-0"><i className="fas fa-shield-alt me-2"></i>Complexity Rules</h6>
                </div>
                <div className="card-body p-0">
                  {(() => {
                    const rules = parseJSON(selectedProfile.complexity_rules);
                    // Filter out non-display fields and map to friendly labels
                    const displayLabels = {
                      'function_name': 'Verify Function',
                      'min_length': 'Minimum Length',
                      'min_letters': 'Minimum Letters',
                      'min_uppercase': 'Minimum Uppercase',
                      'min_lowercase': 'Minimum Lowercase',
                      'min_digits': 'Minimum Digits',
                      'min_special': 'Minimum Special Characters',
                      'require_uppercase': 'Require Uppercase',
                      'require_lowercase': 'Require Lowercase',
                      'require_digit': 'Require Digit',
                      'require_special': 'Require Special Character',
                      'min_differ_from_old': 'Differ from Old Password',
                      'cannot_contain_username': 'Cannot Contain Username',
                      'cannot_contain_reverse_username': 'Cannot Contain Reverse Username',
                      'cannot_contain_db_name': 'Cannot Contain Database Name',
                      'cannot_contain_oracle': 'Cannot Contain "Oracle"'
                    };
                    // Fields to exclude from display
                    const excludeFields = ['source_available', 'function_source_preview', 'function_source', 'note'];
                    const filteredEntries = Object.entries(rules).filter(([key]) => !excludeFields.includes(key));

                    return (
                      <table className="table table-sm table-striped mb-0">
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                          <tr>
                            <th style={{ width: '60%', whiteSpace: 'nowrap' }}>Rule</th>
                            <th style={{ width: '40%' }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEntries.map(([key, value]) => (
                            <tr key={key}>
                              <td style={{ whiteSpace: 'nowrap' }}>{displayLabels[key] || key.replace(/_/g, ' ')}</td>
                              <td>
                                {typeof value === 'boolean' ? (
                                  value ? <span className="text-success">Yes</span> : <span className="text-muted">No</span>
                                ) : (
                                  <strong>{formatActualValue(value)}</strong>
                                )}
                              </td>
                            </tr>
                          ))}
                          {filteredEntries.length === 0 && (
                            <tr><td colSpan="2" className="text-muted text-center">No complexity rules defined</td></tr>
                          )}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </WideModal>

      {/* Custom Policy Modal */}
      <WideModal
        show={showOverrideModal}
        onClose={() => {
          setShowOverrideModal(false);
          setEditingOverride(null);
        }}
        title={editingOverride ? 'Edit Custom Policy' : 'Add Custom Policy'}
        headerIcon="fa-sliders-h"
        headerClass="bg-light border-bottom"
        maxWidth="900px"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowOverrideModal(false);
                setEditingOverride(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={saveOverride}
              disabled={!overrideForm.rule_id}
            >
              {editingOverride ? 'Update Policy' : 'Create Policy'}
            </button>
          </>
        }
      >
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label fw-bold">Benchmark Rule</label>
              <select
                className="form-select"
                value={overrideForm.rule_id}
                onChange={(e) => setOverrideForm({...overrideForm, rule_id: e.target.value})}
              >
                <option value="">Select a rule...</option>
                {benchmarkRules.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.rule_code} - {rule.rule_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Show selected rule details */}
            {overrideForm.rule_id && (
              <div className="alert alert-info mb-3">
                <strong>Current Requirement:</strong>{' '}
                {formatExpectedValue(benchmarkRules.find(r => r.id === parseInt(overrideForm.rule_id))?.expected_value)}
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-bold">Policy Type</label>
              <select
                className="form-select"
                value={overrideForm.override_type}
                onChange={(e) => setOverrideForm({...overrideForm, override_type: e.target.value})}
              >
                <option value="SUPPRESS">Suppress (Skip this rule entirely)</option>
                <option value="CUSTOM_VALUE">Custom Value (Use different requirement)</option>
              </select>
              <small className="text-muted">
                {overrideForm.override_type === 'SUPPRESS'
                  ? 'This rule will be skipped during compliance checks'
                  : 'Replace the standard requirement with your custom value'}
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Applicable Account Types</label>
              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {accountTypeOptions.map((option) => (
                  <div key={option.value} className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`account-type-${option.value}`}
                      checked={overrideForm.account_types.includes(option.value)}
                      onChange={(e) => {
                        let newTypes;
                        if (option.value === 'all') {
                          // If 'all' is selected, clear other selections
                          newTypes = e.target.checked ? ['all'] : [];
                        } else {
                          // If specific type selected, remove 'all'
                          const currentTypes = overrideForm.account_types.filter(t => t !== 'all');
                          if (e.target.checked) {
                            newTypes = [...currentTypes, option.value];
                          } else {
                            newTypes = currentTypes.filter(t => t !== option.value);
                          }
                          // If no types selected, default back to 'all'
                          if (newTypes.length === 0) newTypes = ['all'];
                        }
                        setOverrideForm({...overrideForm, account_types: newTypes});
                      }}
                      disabled={option.value !== 'all' && overrideForm.account_types.includes('all')}
                    />
                    <label className="form-check-label" htmlFor={`account-type-${option.value}`}>
                      <strong>{option.label}</strong>
                      <br />
                      <small className="text-muted">{option.description}</small>
                    </label>
                  </div>
                ))}
              </div>
              <small className="text-muted">
                Select which account types this policy applies to. Select "All Accounts" for universal application, or choose specific types for differentiated requirements.
              </small>
            </div>
          </div>

          <div className="col-md-6">
            {overrideForm.override_type === 'CUSTOM_VALUE' && (
              <div className="mb-3">
                <label className="form-label fw-bold">Custom Expected Value</label>
                <div className="row g-2 mb-2">
                  <div className="col-md-5">
                    <label className="form-label small">Operator</label>
                    <select
                      className="form-select"
                      value={(() => {
                        try {
                          return JSON.parse(overrideForm.custom_expected_value || '{}').operator || '>=';
                        } catch { return '>='; }
                      })()}
                      onChange={(e) => {
                        let current = {};
                        try { current = JSON.parse(overrideForm.custom_expected_value || '{}'); } catch {}
                        current.operator = e.target.value;
                        setOverrideForm({...overrideForm, custom_expected_value: JSON.stringify(current)});
                      }}
                    >
                      <option value=">=">At least (≥)</option>
                      <option value="<=">At most (≤)</option>
                      <option value="==">Exactly (=)</option>
                      <option value="!=">Not equal (≠)</option>
                      <option value=">">Greater than (&gt;)</option>
                      <option value="<">Less than (&lt;)</option>
                      <option value="not_null">Must be configured</option>
                    </select>
                  </div>
                  <div className="col-md-7">
                    <label className="form-label small">Value</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 12, 90, UNLIMITED"
                      value={(() => {
                        try {
                          return JSON.parse(overrideForm.custom_expected_value || '{}').value || '';
                        } catch { return ''; }
                      })()}
                      onChange={(e) => {
                        let current = {};
                        try { current = JSON.parse(overrideForm.custom_expected_value || '{}'); } catch {}
                        // Always ensure operator is set (default to >=)
                        if (!current.operator) current.operator = '>=';
                        current.value = e.target.value;
                        setOverrideForm({...overrideForm, custom_expected_value: JSON.stringify(current)});
                      }}
                    />
                  </div>
                </div>
                <small className="text-muted">
                  Preview: <strong>{formatExpectedValue(overrideForm.custom_expected_value)}</strong>
                </small>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-bold">Justification</label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Explain why this custom policy is needed (e.g., business requirement, compensating control, legacy system limitation)..."
                value={overrideForm.justification}
                onChange={(e) => setOverrideForm({...overrideForm, justification: e.target.value})}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Expiry Date (Optional)</label>
              <input
                type="date"
                className="form-control"
                value={overrideForm.expiry_date}
                onChange={(e) => setOverrideForm({...overrideForm, expiry_date: e.target.value})}
              />
              <small className="text-muted">Leave empty for permanent policy. Expired policies will be flagged for review.</small>
            </div>
          </div>
        </div>
      </WideModal>
    </div>
  );
};

export default OraclePasswordCompliance;
